/**
 * Line-level modifier pricing (F-MOD-N1).
 *
 * A product's chosen modifier options are FOLDED into the line's unit price:
 * a Rs 600 latte with a Rs 150 extra shot is one line at Rs 750, not two
 * lines. The backend does the same fold when it writes order_items.unit_price,
 * so `order_items.total_price === quantity * unit_price` still holds — an
 * identity twelve report queries depend on.
 *
 * This file is one of THREE mirrors of the same arithmetic:
 *
 *   backend/internal/pricing/line_modifiers.go   (the authority — it prices the sale)
 *   frontend/src/lib/lineModifiers.ts            (this file — the counter preview)
 *   storefront/src/lib/lineModifiers.ts          (online ordering preview)
 *
 * They are pinned equal by backend/internal/pricing/testdata/line_modifiers/cases.json,
 * which all three test suites read. Change one, change all three, and add a
 * case. Divergence here means the counter shows one price and the customer is
 * charged another — CLAUDE.md invariant #11.
 *
 * Deliberately separate from counterPricing.ts: modifiers resolve per line,
 * and `computeCartTotals` still takes a scalar subtotal. If a diff makes
 * computeCartTotals aware of modifiers, the design has leaked — reject it.
 */

/** Matches modifier_groups.price_mode. */
export type ModifierPriceMode = 'add' | 'replace'

/** The priced definition of one option, as served by GET /products/modifiers. */
export interface ModifierOptionPrice {
  id: string
  price_delta: number
  price_mode: ModifierPriceMode
}

/**
 * One chosen option on a cart line. An id and a count, and deliberately no
 * price — the client never sends money to the server, and it never stores a
 * price it might later resurrect stale.
 *
 * `quantity` is PER PARENT UNIT: two lattes each with one extra shot is a line
 * of quantity 2 carrying a selection of quantity 1.
 */
export interface LineSelection {
  group_id: string
  option_id: string
  quantity: number
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Fold validated modifier selections into one unit price.
 *
 * Mirrors pricing.ResolveLineUnitPrice step for step:
 *   1. start at basePrice;
 *   2. apply the single 'replace' selection, which SETS the base;
 *   3. add every 'add' selection, delta * per-unit quantity;
 *   4. round to paisa exactly ONCE, at the end;
 *   5. clamp at zero.
 *
 * Rounding once matters — rounding per option drifts from the backend's
 * roundMoney and produces one-paisa disagreements the server then overrides,
 * which reads to a cashier as the POS lying.
 *
 * An option id absent from `optionById` is ignored for pricing; the caller
 * surfaces it as a dropped selection (same discipline restoreDraftItems already
 * applies to a product that left the menu).
 *
 * Two 'replace' selections are a configuration error the database forbids and
 * the handler rejects, but this function must stay total, so it tie-breaks on
 * the lowest option id. An undefined tie-break is how two terminals come to
 * show two different prices.
 */
export function resolveLineUnitPrice(
  basePrice: number,
  selections: ReadonlyArray<Pick<LineSelection, 'option_id' | 'quantity'>>,
  optionById: ReadonlyMap<string, ModifierOptionPrice>,
): number {
  let unit = basePrice

  const live = selections.filter((s) => s.quantity >= 1 && optionById.has(s.option_id))

  // Step 2 — the replace selection, chosen deterministically.
  let replaceId = ''
  for (const s of live) {
    const o = optionById.get(s.option_id)!
    if (o.price_mode !== 'replace') continue
    if (replaceId === '' || o.id < replaceId) replaceId = o.id
  }
  if (replaceId !== '') unit = optionById.get(replaceId)!.price_delta

  // Step 3 — the add selections, sorted so float accumulation order matches Go's.
  const adds = live
    .filter((s) => optionById.get(s.option_id)!.price_mode === 'add')
    .slice()
    .sort((a, b) => (a.option_id < b.option_id ? -1 : a.option_id > b.option_id ? 1 : 0))
  for (const s of adds) {
    unit += optionById.get(s.option_id)!.price_delta * s.quantity
  }

  // Steps 4 and 5.
  unit = round2(unit)
  return unit < 0 ? 0 : unit
}

/**
 * Index the option catalogue for `resolveLineUnitPrice`. Built once per menu
 * load and memoised by the caller — never per cart line.
 */
export function indexModifierOptions(
  options: ReadonlyArray<ModifierOptionPrice>,
): Map<string, ModifierOptionPrice> {
  const m = new Map<string, ModifierOptionPrice>()
  for (const o of options) m.set(o.id, o)
  return m
}
